import { memo } from 'react';
import { AppealStatus, AppealVersion, V2JuryStage } from '../../types/appeals';
import {
  Hourglass, FileText, Bot, Gavel, ArrowRight,
} from 'lucide-react';
import { cn } from '../../utils/cn';

interface AppealWorkflowBannerProps {
  status: AppealStatus;
  /** 2 = Jury V2 two-stage flow; defaults to 1 (legacy) so existing callers are unaffected. */
  appealVersion?: AppealVersion;
  /** Current V2 jury stage (only meaningful while status is in_jury on a V2 appeal). */
  v2JuryStage?: V2JuryStage | null;
  className?: string;
}

type Phase = 'staff' | 'bot' | 'terminal';

interface WorkflowStep {
  key: AppealStatus;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  phase: Phase;
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  { key: 'pending', label: 'Pending Assignment', shortLabel: 'Pending', icon: Hourglass, phase: 'staff' },
  { key: 'awaiting_staff', label: 'Awaiting Staff Answers', shortLabel: 'Staff Review', icon: FileText, phase: 'staff' },
  { key: 'staff_answered', label: 'Queued for Mr.Roboto', shortLabel: 'Mr.Roboto Queue', icon: Bot, phase: 'bot' },
  { key: 'in_jury', label: 'Jury Deliberation', shortLabel: 'In Jury', icon: Gavel, phase: 'bot' },
  { key: 'jury_decided', label: 'Verdict Pending Finalization', shortLabel: 'Finalizing', icon: Gavel, phase: 'bot' },
];

const PHASE_CONFIG: Record<Phase, { label: string; description: string; color: string; bgColor: string; borderColor: string }> = {
  staff: {
    label: 'Staff Action Required',
    description: 'This appeal is waiting for staff input. Use the action buttons above to progress it.',
    color: 'text-orange-300',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
  },
  bot: {
    label: 'Managed by Mr.Roboto — Discord Jury',
    description: 'This appeal has been handed off to Mr.Roboto. A jury is reviewing the case in Discord. Staff can monitor progress here but cannot vote — only escalate or cancel if needed.',
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
  },
  terminal: {
    label: 'Closed',
    description: 'This appeal has reached a final state.',
    color: 'text-glass-400',
    bgColor: 'bg-glass-100/5',
    borderColor: 'border-glass-200/15',
  },
};

const STATUS_PHASE: Record<AppealStatus, Phase> = {
  pending: 'staff',
  awaiting_staff: 'staff',
  staff_answered: 'bot',
  in_jury: 'bot',
  jury_decided: 'bot',
  approved: 'terminal',
  denied: 'terminal',
  escalated: 'terminal',
  cancelled: 'terminal',
};

const STATUS_CONTEXT: Record<AppealStatus, string> = {
  pending: 'Assign a staff reviewer to begin processing this appeal.',
  awaiting_staff: 'The assigned reviewer needs to answer all 10 staff questions before this can move to the jury.',
  staff_answered: 'Staff answers are complete. Mr.Roboto will pick this up automatically and create a jury case.',
  in_jury: 'Jury members are voting in Discord via Mr.Roboto. Results will appear here as votes come in.',
  jury_decided: 'The jury has reached a verdict. Mr.Roboto is finalizing the decision.',
  approved: 'The ban has been overturned by jury vote. The system will lift the ban.',
  denied: 'The ban was upheld by jury vote. The ban remains in effect.',
  escalated: 'This appeal was escalated for senior staff review.',
  cancelled: 'This appeal was cancelled and will not proceed further.',
};

const getStepIndex = (status: AppealStatus): number => {
  const idx = WORKFLOW_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : -1;
};

export const AppealWorkflowBanner = memo(({ status, appealVersion = 1, v2JuryStage, className }: AppealWorkflowBannerProps) => {
  const phase = STATUS_PHASE[status];
  const config = PHASE_CONFIG[phase];
  const isV2InJury = appealVersion === 2 && status === 'in_jury';
  const context = isV2InJury
    ? (v2JuryStage === 2
      ? 'Stage 2 of 2: the jury voted to vary the ban and is now deciding how — remove, reduce, or increase.'
      : 'Stage 1 of 2: the jury is deciding whether to uphold the ban as-is or vary it.')
    : STATUS_CONTEXT[status];
  const currentStepIndex = getStepIndex(status);

  const isEscalatedOrCancelled = status === 'escalated' || status === 'cancelled';
  const isApprovedOrDenied = status === 'approved' || status === 'denied';

  if (isApprovedOrDenied) return null;

  return (
    <div className={cn('alert flex-col items-stretch gap-2', config.bgColor, config.borderColor, className)} role="status">
      <div className="flex items-center gap-2">
        <Bot className={cn('w-4 h-4', config.color)} />
        <span className={cn('font-semibold text-sm', config.color)}>{config.label}</span>
      </div>

      <p className="text-sm text-glass-300">{context}</p>

      {!isEscalatedOrCancelled && (
        <div className="flex items-center gap-1 overflow-x-auto py-1 scrollbar-hide">
          {WORKFLOW_STEPS.map((step, i) => {
            const StepIcon = step.icon;
            const isActive = step.key === status;
            const isCompleted = currentStepIndex >= 0 && i < currentStepIndex;
            const isFuture = currentStepIndex >= 0 && i > currentStepIndex;
            const isBot = step.phase === 'bot';
            const stepLabel = step.key === 'in_jury' && isV2InJury
              ? `Jury Stage ${v2JuryStage ?? 1}/2`
              : step.shortLabel;

            return (
              <div key={step.key} className="flex items-center flex-shrink-0">
                <div className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium',
                  isActive && !isBot && 'bg-orange-500/15 border-orange-500/40 text-orange-300',
                  isActive && isBot && 'bg-purple-500/15 border-purple-500/40 text-purple-300',
                  isCompleted && 'bg-green-500/10 border-green-500/20 text-green-400',
                  isFuture && 'bg-glass-100/5 border-glass-200/10 text-glass-500',
                )} aria-current={isActive ? 'step' : undefined}>
                  <StepIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{stepLabel}</span>
                </div>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <ArrowRight className={cn(
                    'w-3 h-3 mx-0.5 flex-shrink-0',
                    isCompleted ? 'text-green-500/50' : 'text-glass-200/20'
                  )} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
